import { useState, useEffect } from "react";
import {
  getShopCashRegisters,
  createCashRegister,
  updateCashRegister,
  deleteCashRegister,
  getShopBankCards,
  addShopBankCard,
  updateShopBankCard,
  deleteShopBankCard,
} from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCurrentShop,
  setCurrentShop,
  hasPermission,
  getShopOwnerCurrencySync,
  isOwnerInShopMode,
  getCurrentUserPermissions,
} from "@/lib/auth";
import { updateOwnerCurrency, updateShop } from "@/lib/api"; // ← NUEVO IMPORT
import {
  Shield,
  Send,
  Save,
  Loader2,
  Wifi,
  MessageSquare,
  CheckCircle,
  XCircle,
} from "lucide-react";
import {
  updateShopTelegram,
  testTelegramConnection,
  sendTestMessage,
  getTelegramConfig,
  setTelegramConfig,
} from "@/lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { usePlan } from "@/hooks/usePlan";
import { AuthorizationCodeGenerator } from "@/components/admin/AuthorizationCodeGenerator";

export default function ShopSettings() {
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  // ============ VERIFICAR SI ES OWNER EN MODO TIENDA ============
  const isOwnerMode = isOwnerInShopMode();

  // Log para depuración
  console.log("👑 ShopSettings - ¿Es owner en modo tienda?", isOwnerMode);

  // Si es owner, tiene acceso total sin importar permisos
  const hasAccess = isOwnerMode || hasPermission("supervisor");

  if (!hasAccess) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="pt-6 text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Acceso restringido</h3>
            <p className="text-muted-foreground">
              Solo el dueño puede acceder a la configuración.
            </p>
            {isOwnerMode && (
              <p className="text-sm mt-4 text-purple-600">
                👑 Estás en modo owner, deberías tener acceso. Si ves este
                mensaje, contacta al administrador.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
  const currentShop = getCurrentShop();
  const { role } = getCurrentUserPermissions();
  const isAdminOrManager = role === "supervisor" || role === "store_manager";
  const queryClient = useQueryClient();
  const [telegramId, setTelegramId] = useState(
    currentShop?.telegramChatId || "",
  );
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    !!currentShop?.telegramChatId,
  );

  // Nuevos estados para Telegram
  const [testConnectionLoading, setTestConnectionLoading] = useState(false);
  const [testMessageLoading, setTestMessageLoading] = useState(false);
  const [testConnectionResult, setTestConnectionResult] = useState<{
    success: boolean;
    message?: string;
    error?: string;
  } | null>(null);
  const [testMessageResult, setTestMessageResult] = useState<{
    success: boolean;
    message?: string;
    error?: string;
  } | null>(null);

  // Estados para configuración de moneda
  const [currency, setCurrency] = useState(getShopOwnerCurrencySync());
  const [isUpdatingCurrency, setIsUpdatingCurrency] = useState(false);

  // Estados para edición de perfil del negocio
  const [shopName, setShopName] = useState(currentShop?.name || "");
  const [shopAddress, setShopAddress] = useState(currentShop?.address || "");
  const [shopOwner, setShopOwner] = useState(currentShop?.owner || "");
  const [isSavingShop, setIsSavingShop] = useState(false);

  // ============ ESTADOS PARA TARJETAS BANCARIAS ============
  const [bankCards, setBankCards] = useState<any[]>([]);
  const [isLoadingBankCards, setIsLoadingBankCards] = useState(false);
  const [newBankCard, setNewBankCard] = useState({
    cardNumber: "",
    nickname: "",
  });
  const [cardNumberError, setCardNumberError] = useState("");
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [editingCardId, setEditingCardId] = useState<string | null>(null);

  // Formato automático: XXXX-XXXX-XXXX-XXXX
  const formatCardNumber = (value: string) => {
    // Eliminar todo lo que no sea dígito
    const digits = value.replace(/\D/g, "");
    // Limitar a 16 dígitos (CORREGIDO: antes era 12)
    const limited = digits.slice(0, 16); // ← CAMBIAR AQUÍ
    // Añadir guiones cada 4 dígitos
    return limited.replace(/(\d{4})(?=\d)/g, "$1-");
  };

  // Validar formato completo
  const validateCardNumber = (value: string) => {
    return /^\d{4}-\d{4}-\d{4}-\d{4}$/.test(value);
  };

  // Cargar tarjetas al montar o cambiar tienda
  useEffect(() => {
    if (currentShop?.id) {
      loadBankCards();
    }
  }, [currentShop?.id]);

  const loadBankCards = async () => {
    if (!currentShop?.id) return;
    setIsLoadingBankCards(true);
    try {
      const cards = await getShopBankCards(currentShop.id);
      setBankCards(cards);
    } catch (error) {
      console.error("Error cargando tarjetas:", error);
    } finally {
      setIsLoadingBankCards(false);
    }
  };

  const handleAddBankCard = async () => {
    if (!currentShop?.id) return;
    if (!editingCardId && !validateCardNumber(newBankCard.cardNumber)) {
      setCardNumberError("Formato inválido. Use: 1234-5678-9012-3456");
      return;
    }
    setCardNumberError("");
    setIsAddingCard(true);
    try {
      if (editingCardId) {
        await updateShopBankCard(currentShop.id, editingCardId, {
          cardNumber: newBankCard.cardNumber,
          nickname: newBankCard.nickname.trim() || undefined,
        });
        toast({
          title: "✅ Tarjeta actualizada",
          description: "Los datos de la tarjeta se han actualizado",
        });
      } else {
        await addShopBankCard(currentShop.id, {
          cardNumber: newBankCard.cardNumber,
          nickname: newBankCard.nickname.trim() || undefined,
        });
        toast({
          title: "✅ Tarjeta añadida",
          description: "La tarjeta se ha registrado exitosamente",
        });
      }
      setNewBankCard({ cardNumber: "", nickname: "" });
      setEditingCardId(null);
      await loadBankCards();
    } catch (error: any) {
      toast({
        title: "❌ Error",
        description: error.message || "No se pudo guardar la tarjeta",
        variant: "destructive",
      });
    } finally {
      setIsAddingCard(false);
    }
  };

  const handleDeleteBankCard = async (cardId: string, cardNumber: string) => {
    if (!currentShop?.id) return;
    if (!confirm(`¿Eliminar tarjeta ${cardNumber}?`)) return;
    try {
      await deleteShopBankCard(currentShop.id, cardId);
      toast({
        title: "✅ Tarjeta eliminada",
        description: "La tarjeta se ha eliminado exitosamente",
      });
      await loadBankCards();
    } catch (error: any) {
      toast({
        title: "❌ Error",
        description: error.message || "No se pudo eliminar la tarjeta",
        variant: "destructive",
      });
    }
  };
  // ============ FIN ESTADOS Y FUNCIONES DE TARJETAS ============

  // Estados para cajas registradoras
  const [showCreateCashRegisterModal, setShowCreateCashRegisterModal] =
    useState(false);
  const [newCashRegister, setNewCashRegister] = useState({
    id: "", // Para edición
    name: "",
    location: "",
    description: "",
    isEditing: false,
  });

  // Obtener información del plan
  const { can, planType, planName } = usePlan();

  // Determinar si puede usar cajas registradoras (solo Professional y Enterprise)
  const canUseCashRegisters =
    planType === "professional" || planType === "enterprise";

  // Determinar si puede usar Telegram (solo Professional y Enterprise)
  const canUseTelegram =
    planType === "professional" || planType === "enterprise";

  // Query para obtener cajas existentes
  const {
    data: cashRegisters = [],
    isLoading: isLoadingCashRegisters,
    refetch: refetchCashRegisters,
  } = useQuery({
    queryKey: ["cashRegisters", currentShop?.id],
    queryFn: () => getShopCashRegisters(currentShop!.id),
    enabled: !!currentShop,
  });

  // Mutation para crear nueva caja
  const createCashRegisterMutation = useMutation({
    mutationFn: (data: {
      name: string;
      location?: string;
      description?: string;
    }) =>
      createCashRegister({
        shopId: currentShop!.id,
        name: data.name,
        location: data.location,
        description: data.description,
      }),
    onSuccess: () => {
      toast({
        title: "✅ Caja creada",
        description: "La caja registradora ha sido creada exitosamente",
      });
      setShowCreateCashRegisterModal(false);
      setNewCashRegister({
        id: "",
        name: "",
        location: "",
        description: "",
        isEditing: false,
      }); // ← CORREGIDO
      refetchCashRegisters();
    },
    onError: (error) => {
      toast({
        title: "❌ Error",
        description: error.message || "No se pudo crear la caja",
        variant: "destructive",
      });
    },
  });

  // Mutation para actualizar caja (activar/desactivar)
  const updateCashRegisterMutation = useMutation({
    mutationFn: ({
      registerId,
      updates,
    }: {
      registerId: string;
      updates: any;
    }) => updateCashRegister(currentShop!.id, registerId, updates),
    onSuccess: () => {
      toast({
        title: "✅ Caja actualizada",
        description: "Los cambios se han guardado exitosamente",
      });
      refetchCashRegisters();
    },
    onError: (error) => {
      toast({
        title: "❌ Error",
        description: error.message || "No se pudo actualizar la caja",
        variant: "destructive",
      });
    },
  });

  // Mutation para eliminar caja
  const deleteCashRegisterMutation = useMutation({
    mutationFn: (registerId: string) =>
      deleteCashRegister(currentShop!.id, registerId),
    onSuccess: () => {
      toast({
        title: "✅ Caja eliminada",
        description: "La caja registradora ha sido eliminada",
      });
      refetchCashRegisters();
    },
    onError: (error) => {
      toast({
        title: "❌ Error",
        description: error.message || "No se pudo eliminar la caja",
        variant: "destructive",
      });
    },
  });

  // Estado para confirmación de eliminación
  const [cashRegisterToDelete, setCashRegisterToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Estados para tipos de alertas
  const [lowStockAlerts, setLowStockAlerts] = useState(true);
  const [importantSaleAlerts, setImportantSaleAlerts] = useState(true);
  const [dailySummaryAlerts, setDailySummaryAlerts] = useState(true);
  const [newCustomerAlerts, setNewCustomerAlerts] = useState(true);
  // ============ NUEVO ESTADO PARA HORA DEL RESUMEN DIARIO ============
  const [dailySummaryTime, setDailySummaryTime] = useState("20:00");

  // Cargar configuración de Telegram DESDE EL BACKEND (SOLO UNA VEZ)
  useEffect(() => {
    if (!currentShop) return;

    // Si ya se cargó, no volver a cargar
    if (isConfigLoaded) {
      console.log("📡 Configuración ya cargada, omitiendo recarga");
      return;
    }

    const loadTelegramConfig = async () => {
      try {
        console.log(
          "📡 Cargando configuración de Telegram desde el backend...",
        );

        const config = await getTelegramConfig(currentShop.id);

        console.log("✅ Configuración cargada:", config);

        // Si está habilitado y tiene chatId, mostrar los campos
        if (config.enabled && config.chatId) {
          setTelegramId(config.chatId || "");
          setLowStockAlerts(config.lowStockAlerts);
          setImportantSaleAlerts(config.importantSaleAlerts);
          setDailySummaryAlerts(config.dailySummaryAlerts);
          setNewCustomerAlerts(config.newCustomerAlerts);
          setNotificationsEnabled(true);
          setDailySummaryTime(config.dailySummaryTime || "20:00");
        } else {
          // Si no está habilitado, limpiar campos y desactivar
          setTelegramId("");
          setNotificationsEnabled(false);
          setDailySummaryTime("20:00"); // Resetear a valor por defecto
        }

        // Marcar como cargado
        setIsConfigLoaded(true);
      } catch (error) {
        console.error(
          "Error cargando configuración de Telegram desde backend:",
          error,
        );

        // Fallback a localStorage
        const storageKey = `telegram_config_${currentShop.id}`;
        const savedConfig = localStorage.getItem(storageKey);

        if (savedConfig) {
          try {
            const config = JSON.parse(savedConfig);
            if (config.enabled && config.chatId) {
              setTelegramId(config.chatId || "");
              setLowStockAlerts(config.lowStockAlerts !== false);
              setImportantSaleAlerts(config.importantSaleAlerts !== false);
              setDailySummaryAlerts(config.dailySummaryAlerts !== false);
              setNewCustomerAlerts(config.newCustomerAlerts !== false);
              setNotificationsEnabled(true);
            } else {
              setNotificationsEnabled(false);
            }
          } catch (error) {
            console.error(
              "Error cargando configuración de Telegram desde localStorage:",
              error,
            );
          }
        } else {
          setNotificationsEnabled(false);
        }

        setIsConfigLoaded(true);
      }
    };

    loadTelegramConfig();
  }, [currentShop, isConfigLoaded]); // ← AGREGAR isConfigLoaded como dependencia

  // Resetear flag cuando cambia la tienda
  useEffect(() => {
    setIsConfigLoaded(false);
  }, [currentShop?.id]);

  // Resetear configuración cuando cambia el shop
  useEffect(() => {
    if (currentShop) {
      // Limpiar estados cuando cambia el shop
      setTelegramId("");
      setNotificationsEnabled(false);
      setLowStockAlerts(true);
      setImportantSaleAlerts(true);
      setDailySummaryAlerts(true);
      setNewCustomerAlerts(true);
    }
  }, [currentShop?.id]);

  const updateTelegramMutation = useMutation({
    mutationFn: () => updateShopTelegram(currentShop!.id, telegramId),
    onSuccess: () => {
      toast({
        title: "Configuración guardada",
        description: "Tus preferencias de notificación han sido actualizadas.",
      });
      // Update local storage
      if (currentShop) {
        const updatedShop = { ...currentShop, telegramChatId: telegramId };
        localStorage.setItem("currentShop", JSON.stringify(updatedShop));
      }
    },
    onError: () => {
      toast({
        title: "Error",
        description: "No se pudo guardar la configuración",
        variant: "destructive",
      });
    },
  });

  if (!currentShop) return null;
  // Verificar si el shop tiene ownerId (para dueños multi-tienda)
  const isMultiTenant = !!currentShop.ownerId;

  const handleCurrencyUpdate = async () => {
    if (!currentShop?.ownerId) {
      toast({
        title: "Error",
        description: "No se puede identificar al dueño de la tienda",
        variant: "destructive",
      });
      return;
    }

    setIsUpdatingCurrency(true);
    try {
      const result = await updateOwnerCurrency(currentShop.ownerId, currency);

      if (result.success) {
        toast({
          title: "✅ Moneda actualizada",
          description:
            result.message ||
            `Todas las tiendas usarán ${currency} como moneda principal`,
        });

        // Actualizar localStorage para reflejar el cambio inmediatamente
        const shopOwnerJson = localStorage.getItem("shopOwner");
        if (shopOwnerJson) {
          const shopOwner = JSON.parse(shopOwnerJson);
          shopOwner.currency = currency;
          localStorage.setItem("shopOwner", JSON.stringify(shopOwner));
        }

        // También actualizar currentOwner si existe
        const currentOwnerJson = localStorage.getItem("currentOwner");
        if (currentOwnerJson) {
          const currentOwner = JSON.parse(currentOwnerJson);
          currentOwner.currency = currency;
          localStorage.setItem("currentOwner", JSON.stringify(currentOwner));
        }
      } else {
        toast({
          title: "❌ Error",
          description: result.message || "No se pudo actualizar la moneda", // ← CORREGIDO: usar 'message' en lugar de 'error'
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating currency:", error);
      toast({
        title: "❌ Error",
        description: "Error al actualizar la moneda",
        variant: "destructive",
      });
    } finally {
      setIsUpdatingCurrency(false);
    }
  };

  const handleSave = async () => {
    // Mostrar toast de carga
    toast({
      title: "Guardando...",
      description: "Configurando notificaciones de Telegram",
    });

    try {
      let telegramConfig;

      if (notificationsEnabled) {
        // {CORRECCIÓN: Solo validar ChatID, el token es opcional (usa el del sistema)}
        if (!telegramId) {
          toast({
            title: "❌ Falta Chat ID",
            description:
              "Necesitas ingresar tu Chat ID para activar notificaciones",
            variant: "destructive",
          });
          return;
        }

        // Configuración para activar
        telegramConfig = {
          // {CORRECCIÓN: Enviar cadena vacía en lugar de undefined}
          botToken: "",
          chatId: telegramId,
          enabled: true,
          lowStockAlerts,
          importantSaleAlerts,
          dailySummaryAlerts,
          newCustomerAlerts,
          importantSaleThreshold: 500,
          dailySummaryTime: dailySummaryTime,
        };
      } else {
        // Configuración para desactivar (limpiar)
        telegramConfig = {
          botToken: "",
          chatId: "",
          enabled: false,
          lowStockAlerts: true,
          importantSaleAlerts: true,
          dailySummaryAlerts: true,
          newCustomerAlerts: true,
          importantSaleThreshold: 500,
          dailySummaryTime: "20:00",
        };
      }

      console.log("📤 Guardando configuración de Telegram:", telegramConfig);

      // Guardar en el backend
      const result = await setTelegramConfig(currentShop.id, telegramConfig);

      console.log("✅ Respuesta del servidor:", result);

      if (result.success) {
        // Guardar en localStorage como caché
        const storageKey = `telegram_config_${currentShop.id}`;

        if (notificationsEnabled) {
          localStorage.setItem(storageKey, JSON.stringify(telegramConfig));
          toast({
            title: "✅ Notificaciones activadas",
            description:
              "Tu configuración de Telegram ha sido guardada correctamente",
          });
        } else {
          localStorage.removeItem(storageKey);
          // Limpiar los campos
          setTelegramId("");
          toast({
            title: "📵 Notificaciones desactivadas",
            description: "Las notificaciones de Telegram están desactivadas",
          });
        }
      } else {
        toast({
          title: "❌ Error",
          description: result.error || "No se pudo guardar la configuración",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      console.error("❌ Error guardando configuración:", error);
      toast({
        title: "❌ Error",
        description: error.message || "No se pudo guardar la configuración",
        variant: "destructive",
      });
    }
  };

  const handleTelegramConnect = () => {
    // {CORRECCIÓN: Usar el bot oficial del sistema}
    // {Obtener el nombre del bot del token (o usar un nombre fijo)}
    window.open("https://t.me/qvaposbot", "_blank");
    toast({
      title: "Conectando con Telegram...",
      description: "Inicia el bot y escribe /start para obtener tu Chat ID.",
    });
  };

  const handleTestConnection = async () => {
    // {CORRECCIÓN: Solo validar ChatID}
    if (!telegramId) {
      toast({
        title: "Falta Chat ID",
        description: "Necesitas ingresar tu Chat ID para probar la conexión",
        variant: "destructive",
      });
      return;
    }

    setTestConnectionLoading(true);
    setTestConnectionResult(null);

    try {
      const result = await testTelegramConnection(currentShop.id);
      setTestConnectionResult(result);

      if (result.success) {
        toast({
          title: "✅ Conexión exitosa",
          description: "Tu bot de Telegram está configurado correctamente",
        });
      } else {
        toast({
          title: "❌ Error de conexión",
          description: result.error || "No se pudo conectar con Telegram",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "❌ Error",
        description: "Error al probar la conexión",
        variant: "destructive",
      });
    } finally {
      setTestConnectionLoading(false);
    }
  };

  const handleSendTestMessage = async () => {
    // {CORRECCIÓN: Solo validar ChatID}
    if (!telegramId) {
      toast({
        title: "Falta Chat ID",
        description: "Necesitas ingresar tu Chat ID para enviar mensajes",
        variant: "destructive",
      });
      return;
    }

    setTestMessageLoading(true);
    setTestMessageResult(null);

    try {
      const message = "🧪 Prueba de notificación desde QvaPOS";
      const result = await sendTestMessage(currentShop.id, telegramId, message);
      setTestMessageResult(result);

      if (result.success) {
        toast({
          title: "✅ Mensaje enviado",
          description: "El mensaje de prueba se ha enviado a Telegram",
        });
      } else {
        toast({
          title: "❌ Error",
          description: result.error || "No se pudo enviar el mensaje",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "❌ Error",
        description: "Error al enviar el mensaje",
        variant: "destructive",
      });
    } finally {
      setTestMessageLoading(false);
    }
  };

  const handleSaveShop = async () => {
    if (!currentShop) return;
    setIsSavingShop(true);
    try {
      const updated = await updateShop(currentShop.id, {
        name: shopName,
        address: shopAddress,
        owner: shopOwner,
      });
      setCurrentShop(updated);
      toast({
        title: "✅ Guardado",
        description: "Información del negocio actualizada correctamente",
      });
    } catch (error: any) {
      toast({
        title: "❌ Error",
        description: error?.message || "No se pudo actualizar la información",
        variant: "destructive",
      });
    } finally {
      setIsSavingShop(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <div className="flex items-center gap-2 mb-8">
        <h1 className="text-3xl font-heading font-bold uppercase">
          Configuración
        </h1>
        {isOwnerMode && (
          <Badge className="bg-purple-100 text-purple-800 border-purple-200">
            👑 Modo Owner
          </Badge>
        )}
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Perfil del Negocio</CardTitle>
            <CardDescription>
              Información básica de tu punto de venta
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre del Negocio</Label>
              <Input
                value={shopName}
                onChange={(e) => setShopName(e.target.value)}
                data-testid="input-shop-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Dirección</Label>
              <Input
                value={shopAddress}
                onChange={(e) => setShopAddress(e.target.value)}
                data-testid="input-shop-address"
              />
            </div>
            <div className="space-y-2">
              <Label>Propietario</Label>
              <Input
                value={shopOwner}
                onChange={(e) => setShopOwner(e.target.value)}
                data-testid="input-shop-owner"
              />
            </div>
            <Button onClick={handleSaveShop} disabled={isSavingShop}>
              {isSavingShop ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</>
              ) : (
                <><Save className="mr-2 h-4 w-4" /> Guardar Cambios</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Configuración de Moneda */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="text-lg">💱</span>
              Configuración de Moneda
            </CardTitle>
            <CardDescription>
              {isMultiTenant
                ? "La moneda se aplicará a TODAS tus tiendas"
                : "Establece la moneda principal para tu negocio"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Moneda Actual</Label>
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-lg">
                      {(() => {
                        const currencyNames: Record<string, string> = {
                          USD: "Dólar Estadounidense (USD)",
                          EUR: "Euro (EUR)",
                          CUP: "Peso Cubano (CUP)",
                          L: "Lempira Hondureño (L)",
                          MXN: "Peso Mexicano (MXN)",
                        };
                        return currencyNames[currency] || "Peso Cubano (CUP)";
                      })()}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Esta moneda se mostrará en todos los precios de tu tienda
                    </p>
                  </div>
                  <div className="text-2xl font-bold">
                    {(() => {
                      const symbols: Record<string, string> = {
                        USD: "$",
                        EUR: "€",
                        CUP: "$",
                        L: "L",
                        MXN: "$",
                      };
                      return symbols[currency] || "$";
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Cambiar Moneda</Label>
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                <option value="CUP">Peso Cubano (CUP) - $</option>
                <option value="L">Lempira Hondureño (L) - L</option>
                <option value="USD">Dólar Estadounidense (USD) - $</option>
                <option value="EUR">Euro (EUR) - €</option>
                <option value="MXN">Peso Mexicano (MXN) - $</option>
              </select>
              <p className="text-xs text-muted-foreground">
                Al cambiar la moneda, todos los precios se mostrarán en la nueva
                moneda
              </p>
            </div>

            <div className="pt-2">
              <Button
                onClick={handleCurrencyUpdate}
                disabled={
                  isUpdatingCurrency || currency === getShopOwnerCurrencySync()
                }
                className="w-full md:w-auto"
              >
                {isUpdatingCurrency ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Actualizando...
                  </>
                ) : (
                  "Actualizar Moneda"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Configuración de Cajas Registradoras - Solo para Professional y Enterprise */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="text-lg">💼</span>
              Cajas Registradoras
            </CardTitle>
            <CardDescription>
              Gestiona los puntos de venta físicos de tu negocio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!canUseCashRegisters ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                <div className="text-amber-500 mb-3">
                  <span className="text-4xl">🔒</span>
                </div>
                <h4 className="font-medium text-gray-700 mb-2">
                  Funcionalidad no disponible
                </h4>
                <p className="text-sm text-gray-500 mb-4 max-w-sm mx-auto">
                  La gestión de cajas registradoras está disponible en los
                  planes Profesional y Enterprise.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => (window.location.href = "/owner/plans")}
                  className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                >
                  Mejorar Plan
                </Button>
              </div>
            ) : (
              <>
                {isLoadingCashRegisters ? (
                  <div className="text-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    <p className="text-sm text-muted-foreground mt-2">
                      Cargando cajas...
                    </p>
                  </div>
                ) : cashRegisters.length === 0 ? (
                  <div className="text-center py-6 border-2 border-dashed rounded-lg">
                    <div className="text-muted-foreground mb-4">
                      <span className="text-2xl">💼</span>
                      <p className="mt-2">
                        No hay cajas registradoras configuradas
                      </p>
                      <p className="text-sm">
                        Crea tu primera caja para poder abrir turnos
                      </p>
                    </div>
                    <Button
                      onClick={() => {
                        setShowCreateCashRegisterModal(true);
                        setNewCashRegister({
                          id: "",
                          name: "",
                          location: "",
                          description: "",
                          isEditing: false,
                        });
                      }}
                      size="sm"
                    >
                      <span className="mr-2">+</span> Crear Primera Caja
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">Tus Cajas Registradoras</h4>
                      <Button
                        onClick={() => {
                          setShowCreateCashRegisterModal(true);
                          setNewCashRegister({
                            id: "",
                            name: "",
                            location: "",
                            description: "",
                            isEditing: false,
                          });
                        }}
                        size="sm"
                        variant="outline"
                      >
                        <span className="mr-2">+</span> Agregar Caja
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {cashRegisters.map((cashRegister: any) => (
                        <div
                          key={cashRegister.id}
                          className={`p-4 rounded-lg border ${cashRegister.isActive ? "border-green-200 bg-green-50" : "border-gray-200 bg-gray-50"}`}
                        >
                          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-2">
                            <div className="flex-1">
                              <div className="flex flex-row justify-between items-start gap-2">
                                <div className="flex-1 min-w-0">
                                  <h5 className="font-medium">
                                    {cashRegister.name}
                                  </h5>
                                  {cashRegister.location && (
                                    <p className="text-sm text-muted-foreground">
                                      📍 {cashRegister.location}
                                    </p>
                                  )}
                                  {cashRegister.description && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                      {cashRegister.description}
                                    </p>
                                  )}
                                </div>
                                <div
                                  className={`px-2 py-1 rounded text-xs whitespace-nowrap ${cashRegister.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}`}
                                >
                                  {cashRegister.isActive
                                    ? "Activa"
                                    : "Inactiva"}
                                </div>
                              </div>

                              {/* Botones de acción */}
                              <div className="flex flex-wrap gap-2 mt-3">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    updateCashRegisterMutation.mutate({
                                      registerId: cashRegister.id,
                                      updates: {
                                        isActive: !cashRegister.isActive,
                                      },
                                    })
                                  }
                                  disabled={
                                    updateCashRegisterMutation.isPending ||
                                    deleteCashRegisterMutation.isPending
                                  }
                                  className="text-xs h-7"
                                >
                                  {updateCashRegisterMutation.isPending &&
                                  updateCashRegisterMutation.variables
                                    ?.registerId === cashRegister.id ? (
                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                  ) : cashRegister.isActive ? (
                                    "❌ Desactivar"
                                  ) : (
                                    "✅ Activar"
                                  )}
                                </Button>

                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    if (cashRegister.isActive) {
                                      toast({
                                        title: "⚠️ No se puede editar",
                                        description:
                                          "Desactiva la caja primero para poder editarla",
                                        variant: "destructive",
                                      });
                                      return;
                                    }
                                    setNewCashRegister({
                                      id: cashRegister.id,
                                      name: cashRegister.name,
                                      location: cashRegister.location || "",
                                      description: cashRegister.description || "",
                                      isEditing: true,
                                    });
                                    setShowCreateCashRegisterModal(true);
                                  }}
                                  disabled={
                                    updateCashRegisterMutation.isPending ||
                                    deleteCashRegisterMutation.isPending
                                  }
                                  className="text-xs h-7"
                                >
                                  ✏️ Editar
                                </Button>

                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setCashRegisterToDelete({
                                      id: cashRegister.id,
                                      name: cashRegister.name,
                                    })
                                  }
                                  disabled={
                                    updateCashRegisterMutation.isPending ||
                                    deleteCashRegisterMutation.isPending ||
                                    cashRegister.isActive
                                  }
                                  className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50"
                                >
                                  🗑️ Eliminar
                                </Button>
                              </div>
                            </div>
                          </div>
                          <div className="mt-3 text-xs text-muted-foreground flex justify-between">
                            <span>
                              Creada:{" "}
                              {new Date(
                                cashRegister.createdAt,
                              ).toLocaleDateString("es-ES")}
                            </span>
                            {cashRegister.isActive && (
                              <span className="text-green-600 font-medium">
                                ⚠️ Tiene turnos activos
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* ============ CONFIGURACIÓN DE TARJETAS BANCARIAS ============ */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span className="text-lg">💳</span>
              Tarjetas Bancarias para Transferencias
            </CardTitle>
            <CardDescription>
              Registra las tarjetas donde recibirás los pagos por transferencia
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Formulario */}
            <div className="space-y-4 p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">{editingCardId ? "Editar Tarjeta" : "Añadir Nueva Tarjeta"}</h4>
                {editingCardId && (
                  <Button variant="ghost" size="sm" onClick={() => { setEditingCardId(null); setNewBankCard({ cardNumber: "", nickname: "" }); }}>
                    Cancelar edición
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                <Label>Número de Tarjeta *</Label>
                <Input
                  placeholder="1234-5678-9012-3456"
                  value={newBankCard.cardNumber}
                  onChange={(e) => {
                    const formatted = formatCardNumber(e.target.value);
                    setNewBankCard({ ...newBankCard, cardNumber: formatted });
                    if (cardNumberError) setCardNumberError("");
                  }}
                  maxLength={19}
                  className="font-mono"
                />
                {cardNumberError && (
                  <p className="text-sm text-red-600">{cardNumberError}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Formato: XXXX-XXXX-XXXX-XXXX (12 dígitos)
                </p>
              </div>

              <div className="space-y-2">
                <Label>Nombre/Nickname {editingCardId ? "" : "(opcional)"}</Label>
                <Input
                  placeholder="Ej: Tarjeta MLC Principal"
                  value={newBankCard.nickname}
                  onChange={(e) =>
                    setNewBankCard({ ...newBankCard, nickname: e.target.value })
                  }
                  maxLength={100}
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleAddBankCard}
                  disabled={
                    isAddingCard || !validateCardNumber(newBankCard.cardNumber)
                  }
                  className="w-full md:w-auto"
                >
                  {isAddingCard ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />{" "}
                      {editingCardId ? "Guardando..." : "Añadiendo..."}
                    </>
                  ) : (
                    editingCardId ? "Guardar Cambios" : "Añadir Tarjeta"
                  )}
                </Button>
              </div>
            </div>

            {/* Lista de tarjetas */}
            <div className="space-y-3">
              <h4 className="font-medium">Tarjetas Registradas</h4>
              {isLoadingBankCards ? (
                <div className="text-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  <p className="text-sm text-muted-foreground mt-2">
                    Cargando tarjetas...
                  </p>
                </div>
              ) : bankCards.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed rounded-lg text-muted-foreground">
                  <p>No hay tarjetas registradas</p>
                  <p className="text-sm">Añade tu primera tarjeta arriba</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {bankCards.map((card) => (
                    <div
                      key={card.id}
                      className="flex items-center justify-between p-3 bg-background border rounded-lg"
                    >
                      <div>
                        <p className="font-mono font-medium">
                          {card.cardNumber}
                        </p>
                        {card.nickname && (
                          <p className="text-sm text-muted-foreground">
                            {card.nickname}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingCardId(card.id);
                            setNewBankCard({
                              cardNumber: card.cardNumber,
                              nickname: card.nickname || "",
                            });
                            setCardNumberError("");
                          }}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleDeleteBankCard(card.id, card.cardNumber)
                          }
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          Eliminar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        {/* ============ FIN CONFIGURACIÓN DE TARJETAS ============ */}

        {/* ============ AGREGAR TARJETA DE CÓDIGO DE SUPERVISOR AQUÍ ============ */}
        {isAdminOrManager && <AuthorizationCodeGenerator />}
        {/* ============ FIN AGREGAR ============ */}

        {/* Notificaciones Telegram - Solo para Professional y Enterprise */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-500" />
              Notificaciones Telegram
            </CardTitle>
            <CardDescription>
              Recibe alertas automáticas en tu teléfono
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {!canUseTelegram ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                <div className="text-amber-500 mb-3">
                  <span className="text-4xl">🔒</span>
                </div>
                <h4 className="font-medium text-gray-700 mb-2">
                  Funcionalidad no disponible
                </h4>
                <p className="text-sm text-gray-500 mb-4 max-w-sm mx-auto">
                  Las notificaciones por Telegram están disponibles en los
                  planes Profesional y Enterprise.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => (window.location.href = "/owner/plans")}
                  className="bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                >
                  Mejorar Plan
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base">Activar Notificaciones</Label>
                    <p className="text-sm text-muted-foreground">
                      Recibe alertas en tiempo real
                    </p>
                  </div>
                  <Switch
                    checked={notificationsEnabled}
                    onCheckedChange={(checked) => {
                      console.log("🔄 Switch cambiado a:", checked);
                      setNotificationsEnabled(checked);

                      if (checked) {
                        toast({
                          title: "ℹ️ Completa los datos",
                          description:
                            "Ingresa el Token y Chat ID, luego haz clic en 'Guardar Configuración'",
                        });
                      }
                    }}
                    data-testid="switch-notifications"
                  />
                </div>

                {notificationsEnabled && (
                  <div className="space-y-6 pt-4 border-t">
                    {/* Mensaje Informativo Bot Único */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start gap-3">
                        <div className="text-blue-600 text-xl">🤖</div>
                        <div>
                          <p className="text-sm font-medium text-blue-800 mb-1">
                            Bot de Notificaciones Configurado
                          </p>
                          <p className="text-xs text-blue-600">
                            El sistema utiliza un bot único global. Solo
                            necesitas configurar tu <strong>Chat ID</strong>{" "}
                            abajo para empezar a recibir alertas en tu teléfono.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Chat ID */}
                    <div className="space-y-2">
                      <Label>Chat ID</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Ej: 123456789"
                          value={telegramId}
                          onChange={(e) => setTelegramId(e.target.value)}
                          data-testid="input-telegram-id"
                          className="font-mono text-sm"
                        />
                        <Button
                          variant="outline"
                          onClick={handleTelegramConnect}
                          size="sm"
                        >
                          Obtener ID
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Tu ID personal en Telegram (usa @userinfobot)
                      </p>
                    </div>

                    {/* Pruebas de conexión */}
                    <div className="space-y-4 pt-4 border-t">
                      <h4 className="font-medium">Prueba la conexión</h4>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Button
                          variant="outline"
                          onClick={handleTestConnection}
                          disabled={!telegramId || testConnectionLoading} // ← Solo validar ChatID
                          size="sm"
                        >
                          {testConnectionLoading ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <Wifi className="h-3 w-3 mr-1" />
                          )}
                          Probar Conexión
                        </Button>

                        <Button
                          variant="outline"
                          onClick={handleSendTestMessage}
                          disabled={!telegramId || testMessageLoading} // ← Solo validar ChatID
                          size="sm"
                        >
                          {testMessageLoading ? (
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          ) : (
                            <MessageSquare className="h-3 w-3 mr-1" />
                          )}
                          Mensaje de Prueba
                        </Button>
                      </div>

                      {/* Resultados de pruebas */}
                      {(testConnectionResult || testMessageResult) && (
                        <div
                          className={`text-sm p-3 rounded-md ${testConnectionResult?.success || testMessageResult?.success ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}
                        >
                          {testConnectionResult && (
                            <div className="flex items-center gap-2">
                              {testConnectionResult.success ? (
                                <CheckCircle className="h-4 w-4" />
                              ) : (
                                <XCircle className="h-4 w-4" />
                              )}
                              <span>
                                {testConnectionResult.message ||
                                  testConnectionResult.error}
                              </span>
                            </div>
                          )}
                          {testMessageResult && (
                            <div className="flex items-center gap-2 mt-1">
                              {testMessageResult.success ? (
                                <CheckCircle className="h-4 w-4" />
                              ) : (
                                <XCircle className="h-4 w-4" />
                              )}
                              <span>
                                {testMessageResult.message ||
                                  testMessageResult.error}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Tipos de notificaciones */}
                    <div className="space-y-4 pt-4 border-t">
                      <h4 className="font-medium">Tipos de Alertas</h4>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label className="text-sm">Stock bajo</Label>
                            <p className="text-xs text-muted-foreground">
                              Cuando un producto está por debajo del umbral
                            </p>
                          </div>
                          <Switch
                            checked={lowStockAlerts}
                            onCheckedChange={setLowStockAlerts}
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label className="text-sm">
                              Ventas importantes
                            </Label>
                            <p className="text-xs text-muted-foreground">
                              Ventas por encima de $500 CUP
                            </p>
                          </div>
                          <Switch
                            checked={importantSaleAlerts}
                            onCheckedChange={setImportantSaleAlerts}
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label className="text-sm">Resumen diario</Label>
                            <p className="text-xs text-muted-foreground">
                              Al final del día (hora configurable)
                            </p>
                          </div>
                          <Switch
                            checked={dailySummaryAlerts}
                            onCheckedChange={setDailySummaryAlerts}
                          />
                        </div>

                        {/* ============ NUEVO: Selector de hora para resumen diario ============ */}
                        {dailySummaryAlerts && (
                          <div className="flex items-center justify-between pl-6 border-l-2 border-blue-200">
                            <div className="space-y-0.5">
                              <Label className="text-sm">
                                Hora del resumen
                              </Label>
                              <p className="text-xs text-muted-foreground">
                                Selecciona la hora para recibir el resumen
                                diario
                              </p>
                            </div>
                            <input
                              type="time"
                              value={dailySummaryTime}
                              onChange={(e) =>
                                setDailySummaryTime(e.target.value)
                              }
                              className="px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                              step="60"
                            />
                          </div>
                        )}
                        {/* ============ FIN SELECTOR DE HORA ============ */}

                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <Label className="text-sm">Nuevos clientes</Label>
                            <p className="text-xs text-muted-foreground">
                              Cuando se registra un nuevo cliente
                            </p>
                          </div>
                          <Switch
                            checked={newCustomerAlerts}
                            onCheckedChange={setNewCustomerAlerts}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleSave}
                  className="w-full md:w-auto"
                  disabled={updateTelegramMutation.isPending}
                  data-testid="button-save-settings"
                >
                  {updateTelegramMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Guardando...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Guardar Configuración
                    </>
                  )}
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal para crear nueva caja */}
      {showCreateCashRegisterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {newCashRegister.isEditing ? "Editar Caja" : "Crear Nueva Caja"}
              </h3>
              <button
                onClick={() => {
                  setShowCreateCashRegisterModal(false);
                  setNewCashRegister({
                    id: "",
                    name: "",
                    location: "",
                    description: "",
                    isEditing: false,
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="cashRegisterName">Nombre de la Caja *</Label>
                <Input
                  id="cashRegisterName"
                  placeholder="Ej: Caja Principal, Caja 1, Mostrador, etc."
                  value={newCashRegister.name}
                  onChange={(e) =>
                    setNewCashRegister({
                      ...newCashRegister,
                      name: e.target.value,
                    })
                  }
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Un nombre descriptivo para identificar la caja
                </p>
              </div>

              <div>
                <Label htmlFor="cashRegisterLocation">Ubicación</Label>
                <Input
                  id="cashRegisterLocation"
                  placeholder="Ej: Mostrador principal, Entrada, Sala 2, etc."
                  value={newCashRegister.location}
                  onChange={(e) =>
                    setNewCashRegister({
                      ...newCashRegister,
                      location: e.target.value,
                    })
                  }
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Dónde está ubicada físicamente la caja
                </p>
              </div>

              <div>
                <Label htmlFor="cashRegisterDescription">
                  Descripción (opcional)
                </Label>
                <textarea
                  id="cashRegisterDescription"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Notas adicionales sobre esta caja..."
                  value={newCashRegister.description}
                  onChange={(e) =>
                    setNewCashRegister({
                      ...newCashRegister,
                      description: e.target.value,
                    })
                  }
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateCashRegisterModal(false)}
                  disabled={createCashRegisterMutation.isPending}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => {
                    if (!newCashRegister.name.trim()) {
                      toast({
                        title: "❌ Error",
                        description: "El nombre de la caja es requerido",
                        variant: "destructive",
                      });
                      return;
                    }

                    if (newCashRegister.isEditing) {
                      updateCashRegisterMutation.mutate({
                        registerId: newCashRegister.id,
                        updates: {
                          name: newCashRegister.name,
                          location: newCashRegister.location || undefined,
                          description: newCashRegister.description || undefined,
                        },
                      });
                      setShowCreateCashRegisterModal(false);
                      setNewCashRegister({
                        id: "",
                        name: "",
                        location: "",
                        description: "",
                        isEditing: false,
                      });
                    } else {
                      // Crear nueva caja
                      createCashRegisterMutation.mutate({
                        name: newCashRegister.name,
                        location: newCashRegister.location,
                        description: newCashRegister.description,
                      });
                    }
                  }}
                  disabled={
                    createCashRegisterMutation.isPending ||
                    !newCashRegister.name.trim()
                  }
                >
                  {createCashRegisterMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {newCashRegister.isEditing
                        ? "Actualizando..."
                        : "Creando..."}
                    </>
                  ) : newCashRegister.isEditing ? (
                    "Actualizar Caja"
                  ) : (
                    "Crear Caja"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación para eliminar caja */}
      {cashRegisterToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-100 p-2 rounded-full">
                <span className="text-red-600">🗑️</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold">Eliminar Caja</h3>
                <p className="text-sm text-muted-foreground">
                  Esta acción no se puede deshacer
                </p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm">
                ¿Estás seguro de eliminar la caja{" "}
                <span className="font-semibold">
                  "{cashRegisterToDelete.name}"
                </span>
                ?
              </p>
              <p className="text-xs text-red-600 mt-2">
                ⚠️ Advertencia: No se puede eliminar una caja que tenga turnos
                activos. Asegúrate de cerrar todos los turnos primero.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setCashRegisterToDelete(null)}
                disabled={deleteCashRegisterMutation.isPending}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  deleteCashRegisterMutation.mutate(cashRegisterToDelete.id, {
                    onSuccess: () => {
                      setCashRegisterToDelete(null);
                    },
                  });
                }}
                disabled={deleteCashRegisterMutation.isPending}
              >
                {deleteCashRegisterMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Eliminando...
                  </>
                ) : (
                  "Eliminar Permanentemente"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
